/**
 * Sunrise/sunset — a PURE, presentation-only computation (PRD §9 time-of-day
 * ruling; docs/specs/phase-6-today-desktop-v2.md §5; design/screens/
 * today-desktop-v2.md §4.1). Zero event-log involvement, zero domain math,
 * zero I/O: a function of (date, lat/long) only, computed client-side, with
 * NO geolocation prompt (PRD OQ20's stated default) — this module is closer
 * to `format/date.ts`'s pure formatters than to anything in `src/domain`.
 *
 * Uses the standard NOAA solar-position approximation (the same algorithm
 * named as the recommended approach by the phase spec §5) — accurate to
 * within a few minutes for this product's ambient-chrome purpose (not a
 * navigation-grade ephemeris).
 */

/** A fixed reference location, retained for `computeSunTimes`'s own default
 *  parameter/tests only — San Francisco. Callers in the product itself no
 *  longer rely on this as a silent fallback: the Today header now reads a
 *  real Settings-configured city (`@ui/shared/settings/location.ts`,
 *  `getStoredLocation`) and renders the sunrise/sunset pair only when one
 *  is set, never defaulting to this location and presenting it as fact
 *  (design/screens/today-desktop-v2.md §4.1's absent-state ruling). */
export const DEFAULT_SOLAR_LOCATION: SolarLocation = {
  latitude: 37.7749,
  longitude: -122.4194,
  timezone: 'America/Los_Angeles',
};

export interface SolarLocation {
  readonly latitude: number;
  readonly longitude: number;
  /** IANA timezone name (e.g. `'Europe/London'`) for the location itself —
   *  NOT the device's own timezone. Optional only so this stays a strictly
   *  additive change to an already-shipped signature; every real call site
   *  (`@ui/shared/format/cities.ts` entries) supplies one. When omitted,
   *  falls back to the device's own offset (the pre-existing simplification
   *  this field was added to stop being the ONLY option — see
   *  `resolveUtcOffsetMinutes` below for why that fallback is wrong for any
   *  city outside the device's own timezone). */
  readonly timezone?: string;
}

/**
 * Real UTC offset (minutes, positive east of UTC — same sign convention as
 * `-date.getTimezoneOffset()`) for `timeZone` on `date`, resolved via the
 * browser's built-in IANA timezone database (`Intl` — no network call, no
 * bundled tz data, and still a pure function of its arguments). Correctly
 * accounts for that specific zone's DST rules on that specific date.
 *
 * This replaces the bug this module shipped with: `date.getTimezoneOffset()`
 * only ever reflects the OFFSET OF THE DEVICE, never the offset of whatever
 * city the location picker chose. That was invisible as long as every
 * exercised location shared the product's own device timezone (true for the
 * SF default); it broke openly the first time a user picked a city in a
 * different zone (confirmed live with London: showed sunrise 21:51/sunset
 * 14:19, both impossible for London in July) — a plain sign/formula error
 * would have shown up immediately for SF too, so this was specifically a
 * "which timezone" bug, not a "which sign" bug.
 */
function resolveUtcOffsetMinutes(date: Date, timezone: string | undefined): number {
  if (!timezone) return -date.getTimezoneOffset();
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(date);
  const get = (type: string): number => Number(parts.find((p) => p.type === type)?.value ?? 0);
  const asUtc = Date.UTC(get('year'), get('month') - 1, get('day'), get('hour'), get('minute'), get('second'));
  return Math.round((asUtc - date.getTime()) / 60_000);
}

function toRadians(deg: number): number {
  return (deg * Math.PI) / 180;
}

function toDegrees(rad: number): number {
  return (rad * 180) / Math.PI;
}

/** Day-of-year (1-366), local calendar date only — no timezone conversion needed for this approximation. */
function dayOfYear(date: Date): number {
  const start = new Date(date.getFullYear(), 0, 0);
  const diff = date.getTime() - start.getTime();
  return Math.floor(diff / 86_400_000);
}

/**
 * Minutes-from-midnight sunrise/sunset for `date` at `location`, in that
 * LOCATION's own local time (via `location.timezone`'s real UTC offset for
 * that date — DST-correct, resolved through the browser's `Intl` timezone
 * database). If `location.timezone` is omitted, falls back to the device's
 * own offset (only correct when the location happens to share the device's
 * timezone) — every real product call site supplies one.
 *
 * Returns `null` for either edge in the (rare, high-latitude) case where the
 * sun does not rise or set that day — never a fabricated time.
 */
export function computeSunTimes(
  date: Date,
  location: SolarLocation = DEFAULT_SOLAR_LOCATION,
): { readonly sunrise: string | null; readonly sunset: string | null } {
  const { latitude, longitude } = location;
  const n = dayOfYear(date);

  // Fractional year (radians).
  const gamma = (2 * Math.PI * (n - 1)) / 365;

  // Equation of time (minutes) and solar declination (radians) — standard
  // NOAA approximation (Spencer, 1971).
  const eqTime =
    229.18 *
    (0.000075 +
      0.001868 * Math.cos(gamma) -
      0.032077 * Math.sin(gamma) -
      0.014615 * Math.cos(2 * gamma) -
      0.040849 * Math.sin(2 * gamma));
  const decl =
    0.006918 -
    0.399912 * Math.cos(gamma) +
    0.070257 * Math.sin(gamma) -
    0.006758 * Math.cos(2 * gamma) +
    0.000907 * Math.sin(2 * gamma) -
    0.002697 * Math.cos(3 * gamma) +
    0.00148 * Math.sin(3 * gamma);

  const latRad = toRadians(latitude);
  // Standard solar-elevation zenith for sunrise/sunset (accounts for
  // atmospheric refraction + solar radius): 90.833 degrees.
  const zenith = toRadians(90.833);

  const cosHourAngle =
    (Math.cos(zenith) - Math.sin(latRad) * Math.sin(decl)) / (Math.cos(latRad) * Math.cos(decl));

  if (cosHourAngle > 1) return { sunrise: null, sunset: null }; // sun never rises
  if (cosHourAngle < -1) return { sunrise: null, sunset: null }; // sun never sets

  const hourAngle = toDegrees(Math.acos(cosHourAngle));

  // The LOCATION's own real UTC offset (not the device's) when a timezone
  // was supplied; falls back to the device's own offset otherwise.
  const timezoneOffsetMinutes = resolveUtcOffsetMinutes(date, location.timezone);

  const sunriseMinutesUtc = 720 - 4 * (longitude + hourAngle) - eqTime;
  const sunsetMinutesUtc = 720 - 4 * (longitude - hourAngle) - eqTime;

  const sunriseLocal = ((sunriseMinutesUtc + timezoneOffsetMinutes) % 1440 + 1440) % 1440;
  const sunsetLocal = ((sunsetMinutesUtc + timezoneOffsetMinutes) % 1440 + 1440) % 1440;

  return {
    sunrise: formatMinutesAsClock(sunriseLocal),
    sunset: formatMinutesAsClock(sunsetLocal),
  };
}

function formatMinutesAsClock(totalMinutes: number): string {
  const hours = Math.floor(totalMinutes / 60) % 24;
  const minutes = Math.round(totalMinutes % 60);
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}
